import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Drawer,
  Empty,
  Form,
  Input,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { EditOutlined, SearchOutlined } from '@ant-design/icons'
import { Link, useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import fetchAPI from '../../../../lib/utils/fetch-api'
import B2BLayout from '../../../layouts/b2b'
import AuthorizeAsUserButton from '../../../components/authorize-as-user-button'

type NamedOption = { id: number; name: string }
type OfficeOption = NamedOption & { organization_id: number | null }
type RoleOption = NamedOption & { is_active: boolean }
type Membership = {
  organization_id: number | null
  organization_name: string | null
  office_id: number | null
  office_name: string | null
}
type UserRole = { role_id: number; role_name: string }
type UserRow = {
  id: number
  name: string | null
  login: string
  phone: string | null
  is_active: boolean
  deleted_at: string | null
  memberships: Membership[]
  roles: UserRole[]
}
type FilterOptions = {
  organizations: NamedOption[]
  offices: OfficeOption[]
  roles: RoleOption[]
}
type ListResponse = {
  users: UserRow[]
  total: number
  page: number
  page_size: number
}
type EditValues = { name: string; phone: string; is_active: boolean; role_ids: number[] }

const Page = styled.div`
  min-width: 0;
  padding-bottom: 32px;
`

const Heading = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 4px 0 20px;

  h2 {
    margin: 0 0 4px;
    font-size: 22px;
    line-height: 1.3;
    font-weight: 600;
  }
  p {
    margin: 0;
    color: #748096;
  }
`

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 0 0 18px;

  .search {
    width: min(300px, 100%);
  }
  .filter {
    width: 190px;
  }
  .office {
    width: 210px;
  }
  .status {
    width: 150px;
  }

  @media (max-width: 760px) {
    .search,
    .filter,
    .office,
    .status {
      flex: 1 1 180px;
      width: auto;
    }
  }
`

const UserCell = styled.div`
  display: grid;
  gap: 1px;
  line-height: 1.35;
  strong {
    font-weight: 600;
  }
  span {
    color: #8390a2;
  }
`

const Muted = styled.span`
  color: #909baa;
`
const EntityList = styled.div`
  display: grid;
  gap: 3px;
  line-height: 1.35;
`

const RoleText = styled.span`
  display: block;
  max-width: 190px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const DrawerIntro = styled.div`
  padding: 0 0 18px;
  margin-bottom: 20px;
  border-bottom: 1px solid #e9edf2;
  display: grid;
  gap: 3px;
  strong {
    font-size: 15px;
  }
  span {
    color: #7b8799;
  }
`

const uniqueNames = (items: Array<string | null | undefined>) =>
  Array.from(new Set(items.filter((item): item is string => Boolean(item))))

const AdminUsersPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const sessionUserId = useSelector((state: any) => Number(state.session?.sessionData?.user?.id))
  const [options, setOptions] = useState<FilterOptions>({
    organizations: [],
    offices: [],
    roles: [],
  })
  const [filterError, setFilterError] = useState('')
  const [data, setData] = useState<ListResponse>({ users: [], total: 0, page: 1, page_size: 25 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [refreshKey, setRefreshKey] = useState(0)
  const [searchText, setSearchText] = useState(searchParams.get('search') || '')
  const [editingUser, setEditingUser] = useState<UserRow | null>(null)
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm<EditValues>()

  const organizationId = searchParams.get('organization_id') || undefined
  const officeId = searchParams.get('office_id') || undefined
  const roleId = searchParams.get('role_id') || undefined
  const status = searchParams.get('status') || 'all'
  const search = searchParams.get('search') || ''

  const updateParams = useCallback(
    (values: Record<string, string | undefined>, replace = false) => {
      const next = new URLSearchParams(searchParams)
      Object.entries(values).forEach(([key, value]) => {
        if (value) next.set(key, value)
        else next.delete(key)
      })
      setSearchParams(next, { replace })
    },
    [searchParams, setSearchParams],
  )

  useEffect(() => {
    setSearchText(search)
  }, [search])

  useEffect(() => {
    if (searchText.trim() === search) return
    const timer = window.setTimeout(() => {
      updateParams({ search: searchText.trim() || undefined, page: undefined }, true)
    }, 400)
    return () => window.clearTimeout(timer)
  }, [searchText, search, updateParams])

  useEffect(() => {
    let mounted = true
    fetchAPI('/api/admin/users/filters', {}, true).then(result => {
      if (!mounted) return
      if (result.error) setFilterError(result.error.message)
      else setOptions(result as FilterOptions)
    })
    return () => {
      mounted = false
    }
  }, [])

  useEffect(() => {
    let mounted = true
    setLoading(true)
    setError('')
    fetchAPI(`/api/admin/users?${searchParams.toString()}`, {}, true).then(result => {
      if (!mounted) return
      if (result.error) setError(result.error.message)
      else setData(result as ListResponse)
      setLoading(false)
    })
    return () => {
      mounted = false
    }
  }, [searchParams, refreshKey])

  const officeOptions = useMemo(
    () =>
      options.offices.filter(
        office => !organizationId || office.organization_id === Number(organizationId),
      ),
    [options.offices, organizationId],
  )

  const openEditor = useCallback(
    (user: UserRow) => {
      setEditingUser(user)
      form.setFieldsValue({
        name: user.name || '',
        phone: user.phone || '',
        is_active: user.is_active,
        role_ids: user.roles.map(role => role.role_id),
      })
    },
    [form],
  )

  const saveUser = useCallback(
    async (values: EditValues) => {
      if (!editingUser) return
      setSaving(true)
      try {
        const result = await fetchAPI(
          `/api/admin/users/${editingUser.id}`,
          {
            method: 'PATCH',
            body: JSON.stringify(values),
          },
          true,
        )
        if (result.error) {
          message.error(result.error.message)
          return
        }
        message.success('Пользователь сохранён')
        setEditingUser(null)
        setRefreshKey(value => value + 1)
      } finally {
        setSaving(false)
      }
    },
    [editingUser],
  )

  const columns: ColumnsType<UserRow> = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        width: 72,
      },
      {
        title: 'Пользователь',
        key: 'user',
        width: 250,
        render: (_, user) => (
          <UserCell>
            <strong>{user.name || 'Без имени'}</strong>
            <span>{user.login}</span>
          </UserCell>
        ),
      },
      {
        title: 'Телефон',
        dataIndex: 'phone',
        width: 150,
        render: (phone: string | null) => phone || <Muted>—</Muted>,
      },
      {
        title: 'Организации',
        key: 'organizations',
        width: 220,
        render: (_, user) => {
          const names = uniqueNames(user.memberships.map(item => item.organization_name))
          return names.length ? (
            <EntityList>
              {names.map(name => (
                <span key={name}>{name}</span>
              ))}
            </EntityList>
          ) : (
            <Muted>—</Muted>
          )
        },
      },
      {
        title: 'Офисы',
        key: 'offices',
        width: 210,
        render: (_, user) => {
          const names = uniqueNames(user.memberships.map(item => item.office_name))
          return names.length ? (
            <EntityList>
              {names.map(name => (
                <span key={name}>{name}</span>
              ))}
            </EntityList>
          ) : (
            <Muted>—</Muted>
          )
        },
      },
      {
        title: 'Роли',
        key: 'roles',
        width: 190,
        render: (_, user) => {
          if (!user.roles.length) return <Muted>—</Muted>
          const roles = user.roles.map(role => role.role_name).join(', ')
          return (
            <Tooltip title={roles}>
              <RoleText>{roles}</RoleText>
            </Tooltip>
          )
        },
      },
      {
        title: 'Статус',
        key: 'status',
        width: 120,
        render: (_, user) =>
          user.deleted_at ? (
            <Tag>Удалён</Tag>
          ) : user.is_active ? (
            <Tag color="green">Активен</Tag>
          ) : (
            <Tag>Неактивен</Tag>
          ),
      },
      {
        title: 'Действия',
        key: 'actions',
        width: 250,
        fixed: 'right',
        align: 'center',
        render: (_, user) => (
          <Space size={8}>
            <Button
              size="small"
              icon={<EditOutlined />}
              disabled={Boolean(user.deleted_at)}
              onClick={() => openEditor(user)}
            >
              Изменить
            </Button>
            {!user.deleted_at && (
              <AuthorizeAsUserButton userId={user.id} isActive={user.is_active} size="small" />
            )}
          </Space>
        ),
      },
    ],
    [openEditor],
  )

  return (
    <B2BLayout
      breadcrumbs={[
        { href: '/admin', title: 'Админка' },
        { href: '/admin/users', title: 'Пользователи' },
      ]}
    >
      <Page>
        <Heading>
          <div>
            <h2>Пользователи</h2>
            <p>{loading ? 'Загрузка списка…' : `Найдено: ${data.total}`}</p>
          </div>
        </Heading>

        <Filters>
          <Input
            className="search"
            prefix={<SearchOutlined />}
            placeholder="Имя, логин, телефон или ID"
            aria-label="Поиск пользователей"
            allowClear
            value={searchText}
            onChange={event => setSearchText(event.target.value)}
          />
          <Select
            className="filter"
            placeholder="Все организации"
            aria-label="Фильтр по организации"
            allowClear
            showSearch
            optionFilterProp="label"
            value={organizationId}
            options={options.organizations.map(item => ({
              value: String(item.id),
              label: item.name,
            }))}
            onChange={value =>
              updateParams({ organization_id: value, office_id: undefined, page: undefined })
            }
          />
          <Select
            className="office"
            placeholder="Все офисы"
            aria-label="Фильтр по офису"
            allowClear
            showSearch
            optionFilterProp="label"
            value={officeId}
            options={officeOptions.map(item => ({ value: String(item.id), label: item.name }))}
            onChange={value => updateParams({ office_id: value, page: undefined })}
          />
          <Select
            className="filter"
            placeholder="Все роли"
            aria-label="Фильтр по роли"
            allowClear
            showSearch
            optionFilterProp="label"
            value={roleId}
            options={options.roles.map(item => ({ value: String(item.id), label: item.name }))}
            onChange={value => updateParams({ role_id: value, page: undefined })}
          />
          <Select
            className="status"
            aria-label="Фильтр по статусу"
            value={status}
            options={[
              { value: 'all', label: 'Все статусы' },
              { value: 'active', label: 'Активные' },
              { value: 'inactive', label: 'Неактивные' },
              { value: 'deleted', label: 'Удалённые' },
            ]}
            onChange={value =>
              updateParams({ status: value === 'all' ? undefined : value, page: undefined })
            }
          />
          {(organizationId || officeId || roleId || status !== 'all' || search) && (
            <Button
              type="link"
              onClick={() => {
                setSearchText('')
                setSearchParams({})
              }}
            >
              Сбросить
            </Button>
          )}
        </Filters>

        {filterError && (
          <Alert
            type="warning"
            showIcon
            message={`Фильтры недоступны: ${filterError}`}
            style={{ marginTop: 16 }}
          />
        )}
        {error && (
          <Alert
            type="error"
            showIcon
            message={error}
            style={{ marginTop: 16 }}
            action={
              <Button size="small" onClick={() => setRefreshKey(value => value + 1)}>
                Повторить
              </Button>
            }
          />
        )}

        <Table<UserRow>
          rowKey="id"
          columns={columns}
          dataSource={data.users}
          loading={loading}
          size="small"
          scroll={{ x: 'max-content', y: '72vh' }}
          locale={{ emptyText: <Empty description="Пользователи не найдены" /> }}
          pagination={{
            current: data.page,
            pageSize: data.page_size,
            total: data.total,
            position: ['bottomCenter'],
            showSizeChanger: true,
            pageSizeOptions: ['25', '50', '100'],
            showTotal: total => `Всего ${total}`,
            onChange: (nextPage, nextPageSize) =>
              updateParams({ page: String(nextPage), page_size: String(nextPageSize) }),
          }}
        />

        <Drawer
          title="Редактирование пользователя"
          open={Boolean(editingUser)}
          width={460}
          onClose={() => setEditingUser(null)}
          destroyOnClose
          extra={
            editingUser && <Link to={`/employees_new/${editingUser.id}`}>Полная карточка</Link>
          }
        >
          {editingUser && (
            <>
              <DrawerIntro>
                <strong>{editingUser.login}</strong>
                <span>Пользователь № {editingUser.id} · логин не меняется в быстром редакторе</span>
              </DrawerIntro>
              {editingUser.id === sessionUserId && (
                <Alert
                  type="info"
                  showIcon
                  message="Свою учётную запись нельзя отключить или лишить роли администратора."
                  style={{ marginBottom: 20 }}
                />
              )}
              <Form<EditValues>
                form={form}
                layout="vertical"
                onFinish={saveUser}
                requiredMark={false}
              >
                <Form.Item
                  name="name"
                  label="ФИО"
                  rules={[
                    { required: true, whitespace: true, message: 'Укажите имя' },
                    { max: 150, message: 'Не более 150 символов' },
                  ]}
                >
                  <Input placeholder="Имя пользователя" />
                </Form.Item>
                <Form.Item
                  name="phone"
                  label="Телефон"
                  rules={[{ max: 50, message: 'Не более 50 символов' }]}
                >
                  <Input placeholder="Телефон" />
                </Form.Item>
                <Form.Item name="role_ids" label="Роли">
                  <Select
                    mode="multiple"
                    placeholder="Выберите роли"
                    options={options.roles
                      .filter(
                        role =>
                          role.is_active ||
                          editingUser.roles.some(item => item.role_id === role.id),
                      )
                      .map(role => ({ value: role.id, label: role.name }))}
                    optionFilterProp="label"
                  />
                </Form.Item>
                <Form.Item name="is_active" label="Активность" valuePropName="checked">
                  <Switch checkedChildren="Активен" unCheckedChildren="Отключён" />
                </Form.Item>
                <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
                  Организации и офисы редактируются в полной карточке. После изменения ролей
                  пользователю может потребоваться повторный вход.
                </Typography.Paragraph>
                <Space style={{ marginTop: 12 }}>
                  <Button type="primary" htmlType="submit" loading={saving}>
                    Сохранить
                  </Button>
                  <Button onClick={() => setEditingUser(null)}>Отмена</Button>
                </Space>
              </Form>
            </>
          )}
        </Drawer>
      </Page>
    </B2BLayout>
  )
}

export default AdminUsersPage
